import BigNumber from 'bignumber.js'

import type { FieldKey, RowData } from '@/api/types'

/** Which way a view is ordered by a field. Matches the backend's `ViewSort.order`. */
export type SortDirection = 'ASC' | 'DESC'

/**
 * Orders two rows by one field.
 *
 * Returns 0 when the field cannot separate them, which is not the same as the
 * rows being equal: the caller falls through to the next sort, and finally to
 * the row's own order and id, exactly as the backend does.
 */
export type RowComparator = (a: RowData, b: RowData) => number

/**
 * Compares strings the way Postgres' `en` collation does, which is what decides
 * the order the backend sends rows in.
 *
 * Not `<` on strings: that compares code points, so `Z` sorts before `a` and a
 * page fetched from the backend arrives in a different order from one sorted
 * here. The two orders disagreeing is visible as rows jumping when a window is
 * refetched.
 */
export function collatedStringCompare(
  a: string,
  b: string,
  direction: SortDirection,
): number {
  return direction === 'ASC' ? a.localeCompare(b, 'en') : b.localeCompare(a, 'en')
}

/** Reads a cell, treating a missing one as null rather than undefined. */
export function readCell(row: RowData, key: FieldKey): unknown {
  return row[key] ?? null
}

/** Builds a comparator from a projection of the cell to a string. */
export function comparingString(
  key: FieldKey,
  direction: SortDirection,
  project: (value: unknown) => string,
): RowComparator {
  return (a, b) =>
    collatedStringCompare(project(readCell(a, key)), project(readCell(b, key)), direction)
}

/**
 * A cell as a string, so an empty cell sorts before any text.
 *
 * Used by the types whose value is a scalar. An object is rendered as JSON
 * rather than left to `String`, which would make every such cell `[object
 * Object]` and therefore indistinguishable -- a silent way for a sort to do
 * nothing.
 */
export function asString(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

/**
 * Orders by a comparison that cannot see nulls, putting them first ascending.
 *
 * Postgres sorts nulls first ascending and last descending for these types, and
 * a mismatch here shows as empty rows moving to the other end of the view the
 * moment a page is refetched.
 */
export function comparingNullable<T>(
  key: FieldKey,
  direction: SortDirection,
  compare: (a: T, b: T) => number,
): RowComparator {
  return (rowA, rowB) => {
    const a = readCell(rowA, key)
    const b = readCell(rowB, key)

    if (a === b) return 0
    if ((a === null && direction === 'ASC') || (b === null && direction === 'DESC')) {
      return -1
    }
    if ((b === null && direction === 'ASC') || (a === null && direction === 'DESC')) {
      return 1
    }

    const result = compare(a as T, b as T)
    return direction === 'ASC' ? result : -result
  }
}

/** Compares two values the backend serialises as decimal strings. */
export function compareBigNumbers(a: unknown, b: unknown): number {
  const left = new BigNumber(a as BigNumber.Value)
  const right = new BigNumber(b as BigNumber.Value)
  if (left.isEqualTo(right)) return 0
  return left.isLessThan(right) ? -1 : 1
}

/** Compares two values that are already JavaScript numbers. */
export function compareNumbers(a: unknown, b: unknown): number {
  const left = a as number
  const right = b as number
  if (left === right) return 0
  return left < right ? -1 : 1
}

/** Compares two timestamps, whatever precision they were sent at. */
export function compareTimestamps(a: unknown, b: unknown): number {
  const left = new Date(a as string).getTime()
  const right = new Date(b as string).getTime()
  if (left === right) return 0
  return left < right ? -1 : 1
}
